<template>
  <img
    ref="root"
    class="extended-image"
    :class="classObject"
    :src="src"
    :alt="alt"
    :width="width"
    :height="height"
    :style="styleObject"
  />
</template>

<script setup>
import noImage from '@/assets/images/no_image.png'
const props = defineProps({
  /**
   * 画像のパス
   */
    src: {
    type: [String, Array],
    required: true,
  },
  /**
   * 代替テキスト
   */
  alt: {
    type: String,
    default: '',
  },
  /**
   * 幅（width 属性用・数値の場合は単位を含む）
   */
  width: {
    type: Number,
    default: undefined,
  },
  /**
   * 高さ（height 属性用・数値の場合は単位を含む）
   */
  height: {
    type: Number,
    default: undefined,
  },
  /**
   * 幅（CSS width プロパティ用・数値の場合は単位を含む）
   */
  cssWidth: {
    type: String,
    default: 'auto',
  },
  /**
   * 高さ（CSS height プロパティ用・数値の場合は単位を含む）
   */
  cssHeight: {
    type: String,
    default: 'auto',
  },
  /**
   * 最小幅（数値の場合は単位を含む）
   */
  minWidth: {
    type: String,
    default: 'auto',
  },
  /**
   * 最小高さ（数値の場合は単位を含む）
   */
  minHeight: {
    type: String,
    default: 'auto',
  },
  /**
   * 最大幅（数値の場合は単位を含む）
   */
  maxWidth: {
    type: String,
    default: 'none',
  },
  /**
   * 最大高さ（数値の場合は単位を含む）
   */
  maxHeight: {
    type: String,
    default: 'none',
  },
  /**
   * 角丸装飾
   */
  rounded: {
    type: Boolean,
    default: false,
  },
  /**
   * 包含要素へのはめ込み方（CSS プロパティ object-fit と同義）
   */
  objectFit: {
    type: String,
    default: 'fill',
  },
  /**
   * 読み込み中表示を有効にするかどうか
   */
  hasLoading: {
    type: Boolean,
    default: true,
  },
})

const imgElem = ref(null)
const loaded = ref(false)
const root = ref(null)

const classObject = computed(() => {
  return {
    rounded: props.rounded,
    loading: loading.value,
    loaded: loaded.value,
  }
})

const styleObject = computed(() => {
  return {
    // スケルトンのサイズを補助する（ただし実際のサイズとは異なる）
    paddingBottom: loading.value && !props.width ? '100%' : undefined,
    width: props.cssWidth === 'auto' ? undefined : props.cssWidth,
    height: props.cssHeight === 'auto' ? undefined : props.cssHeight,
    minWidth: props.minWidth === 'auto' ? undefined : props.minWidth,
    minHeight: props.minHeight === 'auto' ? undefined : props.minHeight,
    maxWidth: props.maxWidth === 'none' ? undefined : props.maxWidth,
    maxHeight: props.maxHeight === 'none' ? undefined : props.maxHeight,
    objectFit: props.objectFit === 'fill' ? undefined : props.objectFit,
  }
}) 

const loading = computed(() => {
  return props.hasLoading && !loaded.value
})

const isComponentReady = computed(() => {
  return imgElem.value !== null && props.src !== ''
})

watch(() => isComponentReady.value, () => {
  imgElem.value.addEventListener('load', () => {
    loaded.value = true
  })
  imgElem.value.addEventListener('error', () => {
    loaded.value = true
    imgElem.value.src = noImage
  })
  imgElem.value.src = props.src
})
 
onMounted(() => {
  imgElem.value = root.value
})

</script>

<style lang="scss" scoped>
.extended-image {
  &.loading {
    /* prettier-ignore */
    background-image:
      linear-gradient(
        to right,
        #efedeb 0%,
        #ccc 50%,
        #ccc 55%,
        #efedeb 100%
      );

    /* prettier-ignore */
    background-size: 200%;
    animation: skeleton-loading 1.2s linear infinite;
  }

  &.rounded {
    border-radius: 0.5rem;
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 0% 0;
  }

  100% {
    background-position: 200% 0;
  }
}
</style>
